import PropTypes from 'prop-types';
// material
import { alpha, styled } from '@mui/material/styles';
import { Box, Stack, AppBar, Toolbar, IconButton, Button } from '@mui/material';
// components
import Iconify from '../../components/color-utils/Iconify';
//
import Searchbar from './Searchbar';
import AccountPopover from './AccountPopover';
// import LanguagePopover from './LanguagePopover';
import NotificationsPopover from './NotificationsPopover';
import { getUserPreData } from '../../redux/features/authSlice';
import { useDispatch, useSelector } from 'react-redux';
import React, { useEffect } from 'react';
import { Rotate90DegreesCcw } from '@mui/icons-material';
import { Settings } from '../Settings';
import { Icon } from '@iconify/react';
import { Navigate } from '../mui/Buttons/Buttons';
import NotificationPopOverNew from './NotificationPopOverNew';
import { navigateToTodo } from '../../redux/features/intervalTodoSlice';
import { useSnackbar } from 'notistack';
import { openInteTasks } from '../../utils/inteTasksSso';
import InteTasksButton from './InteTasksButton';
import DataAccessButton from './DataAccessButton';
import useHasMenuPath from '../../Hooks/useHasMenuPath';
import useDataAccessConsole from '../../Hooks/useDataAccessConsole';
import TargetsBadge from './TargetsBadge';
import { canOpenTargets } from '../../utils/challengeScope';
// import { navigationUrl } from "../../utils/constants/links";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;
const APPBAR_MOBILE = 64;
const APPBAR_DESKTOP = 60;
const DRAWER_SM_WIDTH = 90;

const RootStyle = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'openSideBar',
})(({ theme, openSideBar }) => ({
  boxShadow: 'none',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)', // Fix on Mobile
  // backgroundColor: alpha(theme.palette.common.white, 0.72),
  backgroundColor: 'white',
  [theme.breakpoints.up('lg')]: {
    width: openSideBar ? `calc(100% - ${DRAWER_WIDTH + 1}px)` : `calc(100% - ${DRAWER_SM_WIDTH + 1}px)`,
  },
}));

const ToolbarStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: APPBAR_MOBILE,
  [theme.breakpoints.up('lg')]: {
    minHeight: APPBAR_DESKTOP,
    padding: theme.spacing(0, 5),
  },
}));

// ----------------------------------------------------------------------

DashboardNavbar.propTypes = {
  onOpenSidebar: PropTypes.func,
};

export default function DashboardNavbar({ onOpenSidebar, user, setOpenSideBar, openSideBar }) {
  const navigationUrl = process.env.REACT_APP_PHP_URL;
  const dispatch = useDispatch();
  const userDetails = useSelector((state) => state.login);
  const loading = useSelector((state) => state.login.isLoading);

  // console.log(openSideBar, "sdfs==========================================");
  const userProfile = JSON.parse(localStorage.getItem('cred'));
  const data = { employee_id: userProfile.profile.userId, employee_type: 'employee' };

  // Local state rather than a redux slice: there is nothing to share, and the
  // only thing the button needs to remember is whether its own request is in
  // flight.
  const [inteTasksLoading, setInteTasksLoading] = React.useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Data Access shares one hook with the my-workspace page that offers the same
  // console, so the SSO handshake cannot drift between the two entry points.
  const { loading: dataAccessLoading, open: handleDataAccess } = useDataAccessConsole();

  // The backend decides who gets the Data Access sidebar entry. The toolbar button
  // is the same privilege, so it follows the menu rather than being granted twice
  // — no menu item, no button.
  const canAccessDataConsole = useHasMenuPath('/my-workspace/data-access');

  // The four tutoring projects, OGD, and Finance. The badge is the only way
  // into Interval Targets, so it renders for everyone who might open it — with
  // or without a challenge of their own — rather than disappearing on a quiet
  // month and taking the portal with it. Department comes from the stored
  // profile, which the portal resolves through the wing chain.
  const canSeeTargets = canOpenTargets(userProfile?.profile?.departmentId);

  // InteTasks — opens in a new tab via the same SSO handshake as the Data
  // Access Console. Local state for the same reason: nothing to share, and the
  // only thing to remember is whether this button's own request is in flight.
  const handleInteTasks = async () => {
    if (inteTasksLoading) return;
    setInteTasksLoading(true);
    try {
      await openInteTasks((message) => enqueueSnackbar(message, { variant: 'error' }));
    } finally {
      setInteTasksLoading(false);
    }
  };

  return (
    <RootStyle openSideBar={openSideBar}>
      <ToolbarStyle>
        <IconButton
          onClick={() => setOpenSideBar(!openSideBar)}
          sx={{
            mr: 1,
            color: 'text.primary',
            // display: { lg: "none" },
          }}
        >
          <div
            style={{
              transition: 'transform .5s',
            }}
          >
            <Icon icon="line-md:chevron-small-double-right" rotate={openSideBar === true ? '180deg' : ''} />
          </div>
        </IconButton>

        <Searchbar />
        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={{ xs: 2, sm: 4 }}>
          {/* TODO — HIDDEN FROM THE TOOLBAR , NOT REMOVED . THE HANDLER AND THE
              navigateToTodo SSO CALL ARE LEFT FULLY WIRED , SO DROPPING THIS
              display:'none' IS ALL IT TAKES TO BRING THE BUTTON BACK. */}
          <Box sx={{ display: 'none' }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() =>
                dispatch(navigateToTodo(data)).then((res) => {
                  if (res.payload.status === 'success') {
                    window.open(`${res.payload.data.url}`, '_blank');
                  }
                })
              }
            >
              TODO
            </Button>
          </Box>

          {/* My Projects — opens in a new tab via the InteTasks SSO handshake.
              Full pill on md and up; icon-only below, where the label would
              crowd the toolbar out. */}
          <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
            <InteTasksButton loading={inteTasksLoading} onClick={handleInteTasks} />
          </Box>
          <Box sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
            <InteTasksButton loading={inteTasksLoading} onClick={handleInteTasks} compact />
          </Box>

          {/* Interval Targets — challenge progress, with a handoff into the
              portal itself. Renders nothing when there are no active challenges,
              so it stays out of the way for most of the year. */}
          {canSeeTargets && (
            <>
              <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                <TargetsBadge />
              </Box>
              <Box sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                <TargetsBadge compact />
              </Box>
            </>
          )}

          {/* Data Access Console — opens in a new tab via SSO handshake.
              Full pill on md and up; icon-only below, where the label would
              crowd the toolbar out. */}
          {canAccessDataConsole && (
            <>
              <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
                <DataAccessButton loading={dataAccessLoading} onClick={handleDataAccess} />
              </Box>
              <Box sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                <DataAccessButton loading={dataAccessLoading} onClick={handleDataAccess} compact />
              </Box>
            </>
          )}

          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Navigate
              rotate={'W'}
              btnName={'ork Space'}
              action={() => {
                window.open(`${navigationUrl}/login/auth/${userProfile?.hashID}`, '_blank');
              }}
            />
          </Box>
          <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
            <IconButton
              onClick={() => {
                window.open(`${navigationUrl}/login/auth/${userProfile?.hashID}`, '_blank');
              }}
            >
              <Icon icon="mdi:rocket-launch-outline" width={22} />
            </IconButton>
          </Box>

          {/* <LanguagePopover /> */}
          <Settings />
          <Box sx={{ paddingTop: 0.5 }}>
            {/* <NotificationsPopover /> */}
            {/* <NotificationPopOverNew /> */}
          </Box>

          <AccountPopover userDetails={userDetails} user={user} loading={loading} setOpenSideBar={setOpenSideBar} />
        </Stack>
      </ToolbarStyle>
    </RootStyle>
  );
}
